/**
 * DebugTexturePlane
 *
 * A utility for placing textured planes in world space for debug visualization.
 * Supports placing ImageData, Canvas, or textures as planes in the 3D scene.
 * Click on a plane to show a UI popup for adjusting transparency.
 */

import * as THREE from 'three';
import { Viewer } from '../viewer/viewer';

export interface DebugPlaneOptions {
  /** Unique type identifier for grouping planes (e.g., 'position-map', 'density-map') */
  type: string;
  /** Optional label shown in the UI */
  label?: string;
  /** Initial opacity (0-1), default 0.8 */
  opacity?: number;
  /** Whether the plane should be double-sided, default true */
  doubleSided?: boolean;
  /** Z offset from the base position to prevent z-fighting, default 0.01 */
  zOffset?: number;
}

interface ManagedPlane {
  mesh: THREE.Mesh;
  type: string;
  label: string;
  material: THREE.MeshBasicMaterial;
  texture: THREE.Texture;
}

/**
 * Manager for debug texture planes in 3D space
 */
export class DebugTexturePlaneManager {
  private viewer: Viewer;
  private planes: Map<string, ManagedPlane> = new Map();
  private planeIdCounter = 0;
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private activePopup: HTMLElement | null = null;
  private clickHandler: ((event: MouseEvent) => void) | null = null;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.setupClickHandler();
  }

  /**
   * Create a plane from ImageData at the given world corners.
   * The corners should be in order: top-left, top-right, bottom-right, bottom-left
   */
  createPlaneFromImageData(imageData: ImageData, corners: THREE.Vector3[], options: DebugPlaneOptions): string {
    if (corners.length !== 4) {
      throw new Error('Corners must have exactly 4 points');
    }

    // Create canvas from ImageData
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context');
    }
    ctx.putImageData(imageData, 0, 0);

    return this.createPlaneFromCanvas(canvas, corners, options);
  }

  /**
   * Create a plane from a canvas at the given world corners.
   * The corners should be in order: top-left, top-right, bottom-right, bottom-left
   */
  createPlaneFromCanvas(canvas: HTMLCanvasElement, corners: THREE.Vector3[], options: DebugPlaneOptions): string {
    if (corners.length !== 4) {
      throw new Error('Corners must have exactly 4 points');
    }

    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.NearestFilter;
    texture.magFilter = THREE.NearestFilter;
    texture.flipY = false; // Canvas textures need flipY=false to display correctly in 3D
    texture.needsUpdate = true;

    return this.createPlaneFromTexture(texture, corners, options);
  }

  /**
   * Create a plane from a texture at the given world corners.
   * The corners should be in order: top-left, top-right, bottom-right, bottom-left
   */
  createPlaneFromTexture(texture: THREE.Texture, corners: THREE.Vector3[], options: DebugPlaneOptions): string {
    if (corners.length !== 4) {
      throw new Error('Corners must have exactly 4 points');
    }

    const { type, label = type, opacity = 0.8, doubleSided = true, zOffset = 0.01 } = options;

    // Generate unique ID
    const id = `debug-plane-${type}-${this.planeIdCounter++}`;

    // Calculate plane dimensions and center from corners
    // corners: [topLeft, topRight, bottomRight, bottomLeft]
    const topLeft = corners[0];
    const topRight = corners[1];
    const bottomRight = corners[2];
    const bottomLeft = corners[3];

    // Calculate center
    const center = new THREE.Vector3().add(topLeft).add(topRight).add(bottomRight).add(bottomLeft).multiplyScalar(0.25);

    // Calculate width and height
    const width = topLeft.distanceTo(topRight);
    const height = topLeft.distanceTo(bottomLeft);

    // Calculate normal (cross product of edges)
    const edge1 = new THREE.Vector3().subVectors(topRight, topLeft);
    const edge2 = new THREE.Vector3().subVectors(bottomLeft, topLeft);
    const normal = new THREE.Vector3().crossVectors(edge1, edge2).normalize();

    // Apply z-offset along normal
    const offsetCenter = center.clone().add(normal.clone().multiplyScalar(zOffset));

    // Create geometry
    const geometry = new THREE.PlaneGeometry(width, height);

    // Create material
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: opacity,
      side: doubleSided ? THREE.DoubleSide : THREE.FrontSide,
      depthWrite: false, // Prevent z-fighting issues
    });

    // Create mesh
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(offsetCenter);

    // Orient the plane to match the corners
    // Calculate rotation from the default plane orientation (facing +Z) to the target normal
    const up = new THREE.Vector3(0, 0, 1);
    const quaternion = new THREE.Quaternion().setFromUnitVectors(up, normal);
    mesh.quaternion.copy(quaternion);

    // Apply rotation around normal to align with corners
    const localRight = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion);
    const targetRight = edge1.clone().normalize();
    const angleCorrection = localRight.angleTo(targetRight);
    const crossCheck = new THREE.Vector3().crossVectors(localRight, targetRight);
    const sign = crossCheck.dot(normal) > 0 ? 1 : -1;
    mesh.rotateOnAxis(normal, sign * angleCorrection);

    // Store plane ID in userData for click detection
    mesh.userData.debugPlaneId = id;
    mesh.userData.debugPlaneType = type;
    mesh.userData.debugPlaneLabel = label;

    // Add to scene
    this.viewer.scene.scene.add(mesh);

    // Store reference
    this.planes.set(id, {
      mesh,
      type,
      label,
      material,
      texture,
    });

    return id;
  }

  /**
   * Remove a specific plane by ID
   */
  removePlane(id: string): boolean {
    const plane = this.planes.get(id);
    if (!plane) return false;

    this.viewer.scene.scene.remove(plane.mesh);
    plane.mesh.geometry.dispose();
    plane.material.dispose();
    plane.texture.dispose();
    this.planes.delete(id);

    return true;
  }

  /**
   * Remove all planes of a specific type
   */
  clearPlanesByType(type: string): number {
    let count = 0;
    const toRemove: string[] = [];

    for (const [id, plane] of this.planes) {
      if (plane.type === type) {
        toRemove.push(id);
      }
    }

    for (const id of toRemove) {
      if (this.removePlane(id)) {
        count++;
      }
    }

    return count;
  }

  /**
   * Remove all debug planes
   */
  clearAll(): number {
    let count = 0;
    const toRemove = Array.from(this.planes.keys());

    for (const id of toRemove) {
      if (this.removePlane(id)) {
        count++;
      }
    }

    return count;
  }

  /**
   * Get all plane IDs of a specific type
   */
  getPlanesByType(type: string): string[] {
    const ids: string[] = [];
    for (const [id, plane] of this.planes) {
      if (plane.type === type) {
        ids.push(id);
      }
    }
    return ids;
  }

  /**
   * Get all unique types and their plane counts
   */
  getTypes(): { type: string; count: number; opacity: number }[] {
    const typeMap = new Map<string, { count: number; opacity: number }>();
    for (const plane of this.planes.values()) {
      const existing = typeMap.get(plane.type);
      if (existing) {
        existing.count++;
      } else {
        typeMap.set(plane.type, { count: 1, opacity: plane.material.opacity });
      }
    }
    return Array.from(typeMap.entries()).map(([type, data]) => ({
      type,
      count: data.count,
      opacity: data.opacity,
    }));
  }

  /**
   * Get total number of planes
   */
  getPlaneCount(): number {
    return this.planes.size;
  }

  /**
   * Set opacity of a specific plane
   */
  setPlaneOpacity(id: string, opacity: number): boolean {
    const plane = this.planes.get(id);
    if (!plane) return false;

    plane.material.opacity = Math.max(0, Math.min(1, opacity));
    return true;
  }

  /**
   * Set opacity of all planes of a specific type
   */
  setTypeOpacity(type: string, opacity: number): number {
    let count = 0;
    for (const [, plane] of this.planes) {
      if (plane.type === type) {
        plane.material.opacity = Math.max(0, Math.min(1, opacity));
        count++;
      }
    }
    return count;
  }

  /**
   * Setup click handler for plane interaction
   */
  private setupClickHandler(): void {
    this.clickHandler = (event: MouseEvent) => {
      // Only handle right-click or ctrl+click for debug planes
      if (!event.ctrlKey && event.button !== 2) return;

      const rect = this.viewer.renderArea.getBoundingClientRect();
      this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      const camera = this.viewer.scene.getActiveCamera();
      this.raycaster.setFromCamera(this.mouse, camera);

      // Get all plane meshes
      const meshes = Array.from(this.planes.values()).map((p) => p.mesh);
      const intersects = this.raycaster.intersectObjects(meshes);

      if (intersects.length > 0) {
        const hit = intersects[0];
        const planeId = hit.object.userData.debugPlaneId;
        if (planeId) {
          event.preventDefault();
          event.stopPropagation();
          this.showPlanePopup(planeId, event.clientX, event.clientY);
        }
      }
    };

    this.viewer.renderArea.addEventListener('contextmenu', this.clickHandler);
    this.viewer.renderArea.addEventListener('click', this.clickHandler);
  }

  /**
   * Show popup UI for adjusting plane properties
   */
  private showPlanePopup(planeId: string, x: number, y: number): void {
    // Close existing popup
    this.closePopup();

    const plane = this.planes.get(planeId);
    if (!plane) return;

    // Create popup container
    const popup = document.createElement('div');
    popup.style.cssText = `
      position: fixed;
      left: ${x}px;
      top: ${y}px;
      background: rgba(0, 0, 0, 0.9);
      border: 1px solid #444;
      border-radius: 8px;
      padding: 12px;
      color: #fff;
      font-family: monospace;
      font-size: 12px;
      z-index: 100000;
      min-width: 200px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    `;

    // Header with label and close button
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px solid #444;
    `;
    header.innerHTML = `
      <span style="font-weight: bold; color: #4CAF50;">🖼️ ${plane.label}</span>
      <button style="
        background: none;
        border: none;
        color: #fff;
        cursor: pointer;
        font-size: 16px;
        padding: 0;
      ">×</button>
    `;
    const closeBtn = header.querySelector('button');
    if (closeBtn) closeBtn.addEventListener('click', () => this.closePopup());
    popup.appendChild(header);

    // Opacity slider
    const opacityRow = document.createElement('div');
    opacityRow.style.cssText = `margin-bottom: 12px;`;
    opacityRow.innerHTML = `
      <label style="display: block; margin-bottom: 4px;">
        Opacity: <span class="opacity-value">${Math.round(plane.material.opacity * 100)}%</span>
      </label>
    `;
    const opacitySlider = document.createElement('input');
    opacitySlider.type = 'range';
    opacitySlider.min = '0';
    opacitySlider.max = '100';
    opacitySlider.value = String(Math.round(plane.material.opacity * 100));
    opacitySlider.style.cssText = `width: 100%; cursor: pointer;`;
    opacitySlider.addEventListener('input', () => {
      const value = parseInt(opacitySlider.value) / 100;
      plane.material.opacity = value;
      const opacityValueEl = opacityRow.querySelector('.opacity-value');
      if (opacityValueEl) opacityValueEl.textContent = `${opacitySlider.value}%`;
    });
    opacityRow.appendChild(opacitySlider);
    popup.appendChild(opacityRow);

    // Type opacity (apply to all of same type)
    const typeRow = document.createElement('div');
    typeRow.style.cssText = `margin-bottom: 12px;`;
    const applyToTypeBtn = document.createElement('button');
    applyToTypeBtn.textContent = `Apply to all "${plane.type}"`;
    applyToTypeBtn.style.cssText = `
      background: #333;
      border: 1px solid #555;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      width: 100%;
      font-size: 11px;
    `;
    applyToTypeBtn.addEventListener('click', () => {
      this.setTypeOpacity(plane.type, plane.material.opacity);
    });
    typeRow.appendChild(applyToTypeBtn);
    popup.appendChild(typeRow);

    // Delete buttons
    const deleteRow = document.createElement('div');
    deleteRow.style.cssText = `
      display: flex;
      gap: 8px;
      padding-top: 8px;
      border-top: 1px solid #444;
    `;

    const deleteBtn = document.createElement('button');
    deleteBtn.textContent = 'Delete';
    deleteBtn.style.cssText = `
      flex: 1;
      background: #c0392b;
      border: none;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
    `;
    deleteBtn.addEventListener('click', () => {
      this.removePlane(planeId);
      this.closePopup();
    });
    deleteRow.appendChild(deleteBtn);

    const deleteTypeBtn = document.createElement('button');
    deleteTypeBtn.textContent = `Delete all "${plane.type}"`;
    deleteTypeBtn.style.cssText = `
      flex: 1;
      background: #8b0000;
      border: none;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
    `;
    deleteTypeBtn.addEventListener('click', () => {
      this.clearPlanesByType(plane.type);
      this.closePopup();
    });
    deleteRow.appendChild(deleteTypeBtn);

    popup.appendChild(deleteRow);

    // Add to document
    document.body.appendChild(popup);
    this.activePopup = popup;

    // Close on click outside
    const closeOnOutsideClick = (e: MouseEvent) => {
      if (!popup.contains(e.target as Node)) {
        this.closePopup();
        document.removeEventListener('mousedown', closeOnOutsideClick);
      }
    };
    setTimeout(() => {
      document.addEventListener('mousedown', closeOnOutsideClick);
    }, 0);
  }

  /**
   * Close the active popup
   */
  private closePopup(): void {
    if (this.activePopup) {
      this.activePopup.remove();
      this.activePopup = null;
    }
  }

  /**
   * Dispose of all resources
   */
  dispose(): void {
    this.clearAll();
    this.closePopup();

    if (this.clickHandler) {
      this.viewer.renderArea.removeEventListener('contextmenu', this.clickHandler);
      this.viewer.renderArea.removeEventListener('click', this.clickHandler);
      this.clickHandler = null;
    }
  }
}
